import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  ArrowRight, 
  MapPin, 
  Instagram, 
  Linkedin, 
  Mail, 
  Sparkles, 
  Check, 
  Users, 
  Compass, 
  TrendingUp, 
  Lock
} from "lucide-react";
import { CrestLogo } from "../components/CrestLogo";
import { HOME } from "../constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  // Intersection Observer for scroll reveal
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    const hiddenElements = document.querySelectorAll(".reveal-on-scroll");
    hiddenElements.forEach((el) => observer.observe(el));

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Form states
  const [waitlistForm, setWaitlistForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organisation: "",
    role: "",
    sector: "",
    linkedin: "",
    whyJoin: "",
  });

  const [nominateForm, setNominateForm] = useState({
    nominatorName: "",
    nominatorEmail: "",
    nomineeName: "",
    organisation: "",
    whyNominate: "",
  });

  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [nominateSubmitting, setNominateSubmitting] = useState(false);
  const [dinners, setDinners] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/dinners`).then((res) => setDinners(res.data)).catch(() => {});
  }, []);

  // Form handlers
  const handleWaitlistChange = (e) => {
    const { name, value } = e.target;
    setWaitlistForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNominateChange = (e) => {
    const { name, value } = e.target;
    setNominateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setWaitlistSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        first_name: waitlistForm.firstName,
        last_name: waitlistForm.lastName,
        email: waitlistForm.email,
        organisation: waitlistForm.organisation,
        role: waitlistForm.role,
        sector: waitlistForm.sector,
        linkedin: waitlistForm.linkedin,
        why_join: waitlistForm.whyJoin,
      });

      toast.success("Interest Registered Successfully", {
        description: `Thank you, ${waitlistForm.firstName}. A confirmation has been sent to ${waitlistForm.email}.`,
        className: "bg-[#F7F5F2] text-[#1F3A33] border-l-4 border-l-[#C8A97E] rounded-none shadow-xl font-sans",
      });

      const firstName = waitlistForm.firstName;
      // Clear form
      setWaitlistForm({
        firstName: "",
        lastName: "",
        email: "",
        organisation: "",
        role: "",
        sector: "",
        linkedin: "",
        whyJoin: "",
      });
      navigate(`/thank-you?type=waitlist&name=${encodeURIComponent(firstName)}`);
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.detail || "Please check your details and try again.";
      toast.error("Submission Failed", {
        description: typeof errMsg === "string" ? errMsg : "Something went wrong.",
        className: "bg-[#F7F5F2] text-red-800 rounded-none shadow-xl font-sans",
      });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleNominateSubmit = async (e) => {
    e.preventDefault();
    setNominateSubmitting(true);
    try {
      await axios.post(`${API}/nominate`, {
        nominator_name: nominateForm.nominatorName,
        nominator_email: nominateForm.nominatorEmail,
        nominee_name: nominateForm.nomineeName,
        organisation: nominateForm.organisation,
        why_nominate: nominateForm.whyNominate,
      });

      toast.success("Nomination Submitted", {
        description: `Thank you. We have sent a confirmation to ${nominateForm.nominatorEmail}.`,
        className: "bg-[#F7F5F2] text-[#1F3A33] border-l-4 border-l-[#C8A97E] rounded-none shadow-xl font-sans",
      });

      const firstName = (nominateForm.nominatorName || "").trim().split(" ")[0];
      // Clear form
      setNominateForm({
        nominatorName: "",
        nominatorEmail: "",
        nomineeName: "",
        organisation: "",
        whyNominate: "",
      });
      navigate(`/thank-you?type=nominate&name=${encodeURIComponent(firstName)}`);
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.detail || "Please check your details and try again.";
      toast.error("Nomination Failed", {
        description: typeof errMsg === "string" ? errMsg : "Something went wrong.",
        className: "bg-[#F7F5F2] text-red-800 rounded-none shadow-xl font-sans",
      });
    } finally {
      setNominateSubmitting(false);
    }
  };

  // Smooth scroll
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#F7F5F2] min-h-screen flex flex-col selection:bg-[#1F3A33] selection:text-[#F7F5F2]">
      <Toaster position="top-right" />

      {/* FLOATING HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F7F5F2]/80 border-b border-[#1F3A33]/10 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <CrestLogo variant="dark" className="w-12 h-12 md:w-10 md:h-10" />
            <span className="font-serif tracking-[0.15em] text-base md:text-lg font-light text-[#1F3A33] uppercase">
              The Catalyst Table
            </span>
          </div>

          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium tracking-widest text-[#1F3A33]/80 uppercase">
            <button onClick={() => scrollToSection("story")} data-testid={HOME.navStoryLink} className="hover:text-[#C8A97E] hover-underline-gold transition-colors">
              Our Story
            </button>
            <button onClick={() => scrollToSection("expectations")} data-testid={HOME.navExpectLink} className="hover:text-[#C8A97E] hover-underline-gold transition-colors">
              What to Expect
            </button>
            <button onClick={() => scrollToSection("experience")} data-testid={HOME.navExperienceLink} className="hover:text-[#C8A97E] hover-underline-gold transition-colors">
              The Experience
            </button>
            <button onClick={() => scrollToSection("dinners")} data-testid={HOME.navDinnersLink} className="hover:text-[#C8A97E] hover-underline-gold transition-colors">
              Upcoming Dinners
            </button>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => scrollToSection("waitlist")} 
              data-testid={HOME.navWaitlistLink}
              className="bg-[#1F3A33] text-[#F7F5F2] text-xs font-semibold tracking-widest uppercase py-3 px-6 hover:bg-[#2E2E2E] transition-all duration-300"
            >
              Pull Up A Seat
            </button>
            <a 
              href="/admin/login" 
              data-testid={HOME.navAdminLink}
              className="border border-[#1F3A33]/20 text-[#1F3A33] hover:bg-[#1F3A33]/5 text-xs p-3 transition-colors"
              title="Admin Control"
            >
              <Lock className="w-4 h-4" />
            </a>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative overflow-hidden pt-12 pb-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8 pr-0 lg:pr-8 text-left">
            <div className="space-y-4">
              <span className="uppercase text-xs tracking-[0.25em] font-bold text-[#8B6A4E] block">
                A Private Members&apos; Dining Community
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.1] text-[#1F3A33]">
                Pull up a seat.<br/>Leave with momentum.
              </h1>
            </div>
            
            <p className="text-base md:text-lg text-[#2E2E2E] font-sans font-light leading-relaxed max-w-xl">
              A curated dinner community where women gather to exchange ideas, build meaningful relationships and open doors for one another.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={() => scrollToSection("waitlist")}
                data-testid={HOME.heroJoinButton}
                className="bg-[#1F3A33] text-[#F7F5F2] uppercase text-xs font-semibold tracking-[0.15em] px-8 py-4 hover:bg-[#2E2E2E] transition-all duration-300 flex items-center justify-center gap-3"
              >
                Join the Waiting List <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollToSection("nominate")}
                data-testid={HOME.heroNominateButton}
                className="border border-[#C8A97E] text-[#1F3A33] uppercase text-xs font-semibold tracking-[0.15em] px-8 py-4 hover:bg-[#C8A97E]/10 transition-all duration-300"
              >
                Nominate a Woman
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 relative w-full h-[380px] md:h-[500px]">
            {/* Elegant thin hairline border around image */}
            <div className="absolute inset-0 border border-[#C8A97E] translate-x-4 translate-y-4 z-0"></div>
            <div className="absolute inset-0 bg-[#1F3A33]/5 z-10"></div>
            <img 
              src="https://images.unsplash.com/photo-1672832593966-fe1795b8d287?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" 
              alt="Women gathered at an intimate candlelit dinner" 
              className="relative w-full h-full object-cover z-20"
            />
          </div>
        </div>
      </section>

      {/* OUR STORY SECTION */}
      <section id="story" className="bg-[#1F3A33]/5 border-y border-[#1F3A33]/10 py-24 md:py-32 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            <div className="lg:col-span-5 relative h-[380px] md:h-[500px] order-last lg:order-first">
              <div className="absolute inset-0 border border-[#C8A97E] -translate-x-4 -translate-y-4 z-0"></div>
              <img 
                src="https://images.unsplash.com/photo-1733341127504-b0a65224f56d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" 
                alt="Two women in warm, meaningful conversation" 
                className="relative w-full h-full object-cover z-10"
              />
            </div>

            <div className="lg:col-span-7 space-y-8 text-left reveal-on-scroll">
              <span className="uppercase text-xs tracking-[0.25em] font-semibold text-[#8B6A4E] block">
                The Narrative
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-[#1F3A33]">
                Why The Catalyst Table Exists
              </h2>
              
              <div className="space-y-6 text-base md:text-lg text-[#2E2E2E] font-sans font-light leading-relaxed">
                <p className="font-serif italic text-[#1F3A33] text-xl md:text-2xl leading-normal border-l-2 border-l-[#C8A97E] pl-6 my-4">
                  “The Catalyst Table was born because another woman opened a door.”
                </p>
                <p>
                  A doctoral opportunity that might have passed me by was shared by a woman who believed I should see it.
                </p>
                <p>
                  It reminded me that some of life’s biggest turning points happen because another woman thought of us, advocated for us or made space for us.
                </p>
                <p className="font-semibold text-[#1F3A33]">
                  The Catalyst Table exists to create more of those moments.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-[#1F3A33]/10 text-sm tracking-wide font-light">
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-[#8B6A4E] uppercase block">I. Career</span>
                    <p className="italic text-[#1F3A33]">“One conversation can change a career.”</p>
                  </div>
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-[#8B6A4E] uppercase block">II. Business</span>
                    <p className="italic text-[#1F3A33]">“One introduction can change a business.”</p>
                  </div>
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-[#8B6A4E] uppercase block">III. Destiny</span>
                    <p className="italic text-[#1F3A33]">“One seat at the table can change a life.”</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT SECTION */}
      <section id="expectations" className="py-24 md:py-32 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center space-y-16">
          <div className="space-y-4 max-w-2xl mx-auto">
            <span className="uppercase text-xs tracking-[0.25em] font-semibold text-[#8B6A4E] block">
              Commitment to Quality
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-[#1F3A33]">
              What to Expect
            </h2>
            <div className="w-12 h-[1px] bg-[#C8A97E] mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-left">
            {/* Item 1 */}
            <div className="space-y-6 p-8 border border-[#1F3A33]/10 hover:border-[#C8A97E]/40 transition-all duration-300 bg-white/40 backdrop-blur-sm reveal-on-scroll">
              <div className="w-12 h-12 flex items-center justify-center bg-[#1F3A33] text-[#F7F5F2]">
                <Compass className="w-5 h-5 stroke-[1.25] text-[#C8A97E]" />
              </div>
              <h3 className="text-2xl font-light text-[#1F3A33]">Meaningful Conversations</h3>
              <p className="text-sm md:text-base text-[#2E2E2E] font-sans font-light leading-relaxed">
                The kind that stay with you long after dinner ends. We leave superficial pleasantries at the door.
              </p>
            </div>

            {/* Item 2 */}
            <div className="space-y-6 p-8 border border-[#1F3A33]/10 hover:border-[#C8A97E]/40 transition-all duration-300 bg-white/40 backdrop-blur-sm reveal-on-scroll" style={{ transitionDelay: "150ms" }}>
              <div className="w-12 h-12 flex items-center justify-center bg-[#1F3A33] text-[#F7F5F2]">
                <Users className="w-5 h-5 stroke-[1.25] text-[#C8A97E]" />
              </div>
              <h3 className="text-2xl font-light text-[#1F3A33]">Intentional Connections</h3>
              <p className="text-sm md:text-base text-[#2E2E2E] font-sans font-light leading-relaxed">
                Every seat is carefully curated. We align diners intentionally to spark dynamic exchange and shared opportunities.
              </p>
            </div>

            {/* Item 3 */}
            <div className="space-y-6 p-8 border border-[#1F3A33]/10 hover:border-[#C8A97E]/40 transition-all duration-300 bg-white/40 backdrop-blur-sm reveal-on-scroll" style={{ transitionDelay: "300ms" }}>
              <div className="w-12 h-12 flex items-center justify-center bg-[#1F3A33] text-[#F7F5F2]">
                <TrendingUp className="w-5 h-5 stroke-[1.25] text-[#C8A97E]" />
              </div>
              <h3 className="text-2xl font-light text-[#1F3A33]">Momentum</h3>
              <p className="text-sm md:text-base text-[#2E2E2E] font-sans font-light leading-relaxed">
                Leave with new ideas, relationships and opportunities. Not a stack of cards, but genuine momentum for your aspirations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE EXPERIENCE SECTION */}
      <section id="experience" className="bg-[#1F3A33]/5 border-y border-[#1F3A33]/10 py-24 md:py-32 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <span className="uppercase text-xs tracking-[0.25em] font-semibold text-[#8B6A4E] block">
              An Elevated Environment
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-[#1F3A33]">
              The Dining Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Curated dinners", desc: "Intentionally matched guest lists ensuring a wealth of backgrounds and mutual value." },
              { title: "Remarkable women", desc: "A sanctuary of leading professionals, innovators, researchers, and creators." },
              { title: "Honest conversations", desc: "A safe container to share authentic challenges, triumphs, and ambitions." },
              { title: "Beautiful settings", desc: "Private candlelit dining chambers in carefully selected luxury hideaways." },
              { title: "No sales pitches", desc: "A strict anti-transactional covenant. We gather to elevate, not to sell." },
              { title: "No awkward networking", desc: "Frictionless hosting that eliminates transactional business-card posturing." },
            ].map((exp, idx) => (
              <div 
                key={idx} 
                className="bg-[#F7F5F2] border border-[#1F3A33]/10 p-8 md:p-10 hover:-translate-y-1 transition-transform duration-500 flex flex-col justify-between"
              >
                <div className="space-y-4 text-left">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#1F3A33]/10 text-[#C8A97E]">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <h4 className="font-serif text-xl md:text-2xl font-light text-[#1F3A33]">
                    {exp.title}
                  </h4>
                  <p className="text-xs md:text-sm text-[#2E2E2E]/80 font-sans font-light leading-relaxed">
                    {exp.desc}
                  </p>
                </div>
              </div>
            ))}

            <div className="bg-[#1F3A33] border border-[#C8A97E]/20 p-8 md:p-10 lg:col-span-3 text-center flex flex-col items-center justify-center space-y-4">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">
                The Golden Rule
              </span>
              <p className="font-serif text-2xl md:text-3xl text-[#F7F5F2] font-light max-w-2xl leading-normal italic">
                “No expectations other than to come as you are.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PASS THE SEAT FORWARD SECTION */}
      <section className="py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            <div className="lg:col-span-7 space-y-8 text-left reveal-on-scroll">
              <span className="uppercase text-xs tracking-[0.25em] font-semibold text-[#8B6A4E] block">
                Generational Momentum
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-[#1F3A33]">
                Women opening doors for women.
              </h2>
              
              <div className="space-y-6 text-base md:text-lg text-[#2E2E2E] font-sans font-light leading-relaxed">
                <p>
                  At the end of every Catalyst Table dinner, each woman is invited to nominate another remarkable woman to pull up a seat.
                </p>
                <p className="font-serif italic text-xl text-[#1F3A33] border-l-2 border-[#C8A97E] pl-6 my-6">
                  “Because we believe that opportunities should be shared, not guarded.”
                </p>
                <p>
                  The community grows one thoughtful introduction at a time. No open calls or public advertisements—only direct, esteemed recognition.
                </p>
              </div>

              <div className="pt-4">
                <button
                  onClick={() => scrollToSection("nominate")}
                  className="bg-[#1F3A33] text-[#F7F5F2] uppercase text-xs font-semibold tracking-[0.15em] px-8 py-4 hover:bg-[#2E2E2E] transition-all duration-300"
                >
                  Pass A Seat Forward
                </button>
              </div>
            </div>

            <div className="lg:col-span-5 flex items-center justify-center py-12">
              <div className="relative w-full max-w-sm h-[420px]">
                <div className="absolute inset-0 border border-[#C8A97E] translate-x-4 translate-y-4 z-0"></div>
                <img
                  src="https://images.unsplash.com/photo-1529516222410-a269d812f320?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Intimate candlelit tablescape with white flowers"
                  className="relative w-full h-full object-cover z-10"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* UPCOMING DINNERS SECTION */}
      <section id="dinners" className="bg-[#1F3A33]/5 border-y border-[#1F3A33]/10 py-24 md:py-32 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <span className="uppercase text-xs tracking-[0.25em] font-semibold text-[#8B6A4E] block">
              Curated Chapters
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-[#1F3A33]">
              Upcoming Dinners
            </h2>
            <p className="text-xs md:text-sm tracking-wide text-[#2E2E2E]/70 font-sans font-light">
              Limited seats. Highly aligned cohorts. Intimate gatherings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dinners.length === 0 ? (
              <p className="col-span-full text-center text-sm text-[#2E2E2E]/60 font-sans font-light py-8">
                New dinners are being curated. Please check back soon.
              </p>
            ) : dinners.map((dinner) => (
              <div
                key={dinner._id || dinner.id}
                data-testid={`dinner-card-${dinner._id || dinner.id}`}
                className={`bg-[#F7F5F2] p-8 flex flex-col justify-between hover:border-[#1F3A33] transition-all duration-500 text-left relative ${dinner.badge ? "border border-[#C8A97E]/30" : "border border-[#1F3A33]/10"}`}
              >
                {dinner.badge ? (
                  <div className="absolute top-0 right-0 bg-[#C8A97E] text-[#1F3A33] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1">
                    {dinner.badge}
                  </div>
                ) : null}
                <div className="space-y-6">
                  {dinner.cohort_label ? (
                    <span className="text-xs font-semibold text-[#8B6A4E] uppercase tracking-widest block">{dinner.cohort_label}</span>
                  ) : null}
                  <h4 className="font-serif text-2xl md:text-3xl font-light text-[#1F3A33]">{dinner.theme}</h4>
                  <p className="text-sm font-sans font-light text-[#2E2E2E]/80 leading-relaxed">
                    {dinner.description}
                  </p>
                </div>
                <div className="pt-8 border-t border-[#1F3A33]/10 mt-8 text-xs text-[#8B6A4E] uppercase tracking-wider flex justify-between gap-4">
                  <span>{dinner.date_label}</span>
                  <span>{dinner.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST SECTION (High Contrast Forest Green) */}
      <section id="waitlist" className="bg-[#1F3A33] text-[#F7F5F2] py-24 md:py-32 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-left space-y-12">
          <div className="space-y-4 text-center md:text-left">
            <span className="uppercase text-xs tracking-[0.25em] font-bold text-[#C8A97E] block">
              Expression of Interest
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-[#F7F5F2]">
              Pull up a seat.
            </h2>
            <div className="w-12 h-[1px] bg-[#C8A97E] my-4"></div>
            <p className="text-sm md:text-base text-[#F7F5F2]/70 font-sans font-light leading-relaxed max-w-2xl">
              Applications are reviewed carefully to preserve the intimacy and alignment of each dinner.
            </p>
          </div>

          <form onSubmit={handleWaitlistSubmit} data-testid={HOME.waitlistForm} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* First Name */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={waitlistForm.firstName}
                  onChange={handleWaitlistChange}
                  required
                  data-testid={HOME.waitlistFirstName}
                  className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>

              {/* Last Name */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={waitlistForm.lastName}
                  onChange={handleWaitlistChange}
                  required
                  data-testid={HOME.waitlistLastName}
                  className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Email */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={waitlistForm.email}
                  onChange={handleWaitlistChange}
                  required
                  data-testid={HOME.waitlistEmail}
                  className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>

              {/* Organisation */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                  Organisation
                </label>
                <input
                  type="text"
                  name="organisation"
                  value={waitlistForm.organisation}
                  onChange={handleWaitlistChange}
                  required
                  data-testid={HOME.waitlistOrganisation}
                  className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Role */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                  Role
                </label>
                <input
                  type="text"
                  name="role"
                  value={waitlistForm.role}
                  onChange={handleWaitlistChange}
                  required
                  data-testid={HOME.waitlistRole}
                  className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>

              {/* Sector / Industry */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                  Sector / Industry
                </label>
                <input
                  type="text"
                  name="sector"
                  value={waitlistForm.sector}
                  onChange={handleWaitlistChange}
                  required
                  data-testid={HOME.waitlistSector}
                  className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>
            </div>

            {/* LinkedIn Profile */}
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                LinkedIn Profile
              </label>
              <input
                type="url"
                name="linkedin"
                value={waitlistForm.linkedin}
                placeholder="https://linkedin.com/in/..."
                onChange={handleWaitlistChange}
                required
                data-testid={HOME.waitlistLinkedin}
                className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
              />
            </div>

            {/* Why Join */}
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-widest text-[#C8A97E] font-medium block">
                Why would you like to join The Catalyst Table?
              </label>
              <textarea
                name="whyJoin"
                value={waitlistForm.whyJoin}
                onChange={handleWaitlistChange}
                required
                rows="4"
                data-testid={HOME.waitlistWhyJoin}
                className="bg-transparent border-0 border-b border-[#F7F5F2]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#F7F5F2] placeholder:text-[#F7F5F2]/30 focus:ring-0 shadow-none outline-none transition-colors w-full resize-none"
              ></textarea>
            </div>

            <p className="text-xs text-[#F7F5F2]/50 font-sans font-light leading-relaxed border-t border-[#F7F5F2]/10 pt-6" data-testid="waitlist-gdpr-statement">
              By submitting this form, you consent to The Catalyst Table storing your information to manage your application and communicate with you about future dinners and membership opportunities. Your information will never be sold or shared with third parties. You may withdraw your consent at any time by contacting <a href="mailto:hello@thecatalysttable.com" className="text-[#C8A97E] hover:underline">hello@thecatalysttable.com</a>.
            </p>

            <div className="pt-4 text-center">
              <button
                type="submit"
                disabled={waitlistSubmitting}
                data-testid={HOME.waitlistSubmit}
                className="w-full md:w-auto bg-[#C8A97E] text-[#1F3A33] uppercase text-xs font-semibold tracking-[0.2em] px-12 py-4 hover:bg-[#F7F5F2] hover:text-[#1F3A33] transition-all duration-300 disabled:opacity-50"
              >
                {waitlistSubmitting ? "Registering..." : "Express Interest"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* NOMINATE A WOMAN SECTION (Warm Ivory Backdrop) */}
      <section id="nominate" className="py-24 md:py-32 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-left space-y-12">
          <div className="space-y-4 text-center md:text-left">
            <span className="uppercase text-xs tracking-[0.25em] font-semibold text-[#8B6A4E] block">
              Pass The Seat Forward
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-[#1F3A33]">
              Someone opened a door for you.
            </h2>
            <p className="font-serif italic text-xl md:text-2xl text-[#1F3A33]/80 font-light max-w-2xl">
              Who deserves a seat at this table because of your recommendation?
            </p>
            <div className="w-12 h-[1px] bg-[#1F3A33]/20 my-4"></div>
          </div>

          <form onSubmit={handleNominateSubmit} data-testid={HOME.nominateForm} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Your Name */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                  Your Name
                </label>
                <input
                  type="text"
                  name="nominatorName"
                  value={nominateForm.nominatorName}
                  onChange={handleNominateChange}
                  required
                  data-testid={HOME.nominateNominatorName}
                  className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>

              {/* Your Email */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                  Your Email Address
                </label>
                <input
                  type="email"
                  name="nominatorEmail"
                  value={nominateForm.nominatorEmail}
                  onChange={handleNominateChange}
                  required
                  data-testid={HOME.nominateNominatorEmail}
                  className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Name of the Woman You Wish to Nominate */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                  Name of the Woman You Wish to Nominate
                </label>
                <input
                  type="text"
                  name="nomineeName"
                  value={nominateForm.nomineeName}
                  onChange={handleNominateChange}
                  required
                  data-testid={HOME.nominateNomineeName}
                  className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>

              {/* Nominee Organisation */}
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                  Her Organisation
                </label>
                <input
                  type="text"
                  name="organisation"
                  value={nominateForm.organisation}
                  onChange={handleNominateChange}
                  required
                  data-testid={HOME.nominateOrganisation}
                  className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full"
                />
              </div>
            </div>

            {/* Why Nominate */}
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                Why should she pull up a seat?
              </label>
              <textarea
                name="whyNominate"
                value={nominateForm.whyNominate}
                onChange={handleNominateChange}
                required
                rows="4"
                data-testid={HOME.nominateWhyNominate}
                className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full resize-none"
              ></textarea>
            </div>

            <div className="pt-4 text-center">
              <button
                type="submit"
                disabled={nominateSubmitting}
                data-testid={HOME.nominateSubmit}
                className="w-full md:w-auto bg-[#1F3A33] text-[#F7F5F2] uppercase text-xs font-semibold tracking-[0.2em] px-12 py-4 hover:bg-[#2E2E2E] transition-all duration-300 disabled:opacity-50"
              >
                {nominateSubmitting ? "Submitting Nominee..." : "Nominate a Woman"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1F3A33] text-[#F7F5F2] pt-20 pb-12 mt-auto text-left border-t border-[#C8A97E]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 pb-16">
          
          <div className="md:col-span-6 space-y-6">
            <div className="flex items-center gap-3">
              <CrestLogo className="w-12 h-12" color="#C8A97E" />
              <span className="font-serif tracking-[0.2em] text-xl font-light uppercase">
                The Catalyst Table
              </span>
            </div>
            <p className="font-serif italic text-lg text-[#C8A97E] font-light">
              “Pull up a seat. Leave with momentum.”
            </p>
            <p className="text-xs text-[#F7F5F2]/60 font-sans tracking-wide">
              Women intentionally opening doors for one another.
            </p>
          </div>

          <div className="md:col-span-3 space-y-4">
            <h5 className="text-xs uppercase tracking-widest text-[#C8A97E] font-bold">Location</h5>
            <p className="text-sm font-sans font-light flex items-center gap-2 text-[#F7F5F2]/80">
              <MapPin className="w-4 h-4 text-[#C8A97E]" /> Leicester, United Kingdom
            </p>
          </div>

          <div className="md:col-span-3 space-y-4">
            <h5 className="text-xs uppercase tracking-widest text-[#C8A97E] font-bold">Connect</h5>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/the_catalyst_table?igsh=dHhpNGxmdGc3ZmJ5&utm_source=qr" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="p-3 bg-[#F7F5F2]/10 hover:bg-[#C8A97E]/20 transition-all text-[#C8A97E]">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-[#F7F5F2]/10 hover:bg-[#C8A97E]/20 transition-all text-[#C8A97E]">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="mailto:hello@thecatalysttable.com" className="p-3 bg-[#F7F5F2]/10 hover:bg-[#C8A97E]/20 transition-all text-[#C8A97E]">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 border-t border-[#F7F5F2]/10 flex flex-col md:flex-row items-center justify-between text-xs text-[#F7F5F2]/40 gap-4">
          <p>© {new Date().getFullYear()} The Catalyst Table. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-[#C8A97E] transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
